import { useTranslations } from '@vocab/react';
import {
  Stack,
  Text,
  HiddenVisually,
  Heading,
  TextLink,
  TextLinkButton,
  Badge,
  Columns,
  Column,
  Hidden,
} from 'braid-design-system';
import type { JSX, ReactNode } from 'react';

import { Salary } from 'src/components/Salary/Salary';
import { JOB_TYPE } from 'src/const/app';

import translations from './GeneralDetails.vocab';

interface Props {
  link: string;
  isActive: boolean;
  title: string;
  listedDate: string;
  advertiser: string;
  location: string;
  salary?: string;
  currencyLabel?: string;
  abstract?: string;
  type: 'applied' | 'saved';
  handleOpenViewApplication: () => void;
  menu: ReactNode;
  actions?: ReactNode;
}

export const GeneralDetails = (props: Props): JSX.Element => {
  const {
    link,
    isActive,
    title,
    listedDate,
    advertiser,
    location,
    salary,
    currencyLabel,
    abstract,
    type,
    handleOpenViewApplication,
    menu,
    actions,
  } = props;
  const { t } = useTranslations(translations);

  const LinkComponent =
    type === JOB_TYPE.applied ? (
      <TextLinkButton onClick={handleOpenViewApplication}>
        <HiddenVisually>{`${t('Job Title')} `}</HiddenVisually>
        {title}
      </TextLinkButton>
    ) : (
      <TextLink href={link}>
        <HiddenVisually>{`${t('Job Title')} `}</HiddenVisually>
        {title}
      </TextLink>
    );

  return (
    <Stack space={type === JOB_TYPE.applied ? 'small' : 'medium'}>
      <Columns space="small">
        <Column>
          <Stack space="medium">
            {!isActive ? (
              <Badge tone="neutral" bleedY>
                {t('Expired')}
              </Badge>
            ) : null}
            <Stack space="small">
              <Heading level="4">{LinkComponent}</Heading>
              <Text>
                <HiddenVisually>{`${t('Advertiser')} `}</HiddenVisually>
                {advertiser}
              </Text>
            </Stack>
          </Stack>
        </Column>
        <Column width="content">{menu}</Column>
      </Columns>

      {type === JOB_TYPE.saved && (
        <Text>{t('Posted', { fromnow: listedDate || '' })}</Text>
      )}

      <Stack space="small">
        <Text>
          <HiddenVisually>{`${t('Location')} `}</HiddenVisually>
          {location}
        </Text>
        {salary ? (
          <Salary salary={salary} currencyLabel={currencyLabel} />
        ) : null}
      </Stack>

      {type === JOB_TYPE.saved && Boolean(abstract) ? (
        <Hidden below="tablet">
          <Text tone="secondary">{abstract}</Text>
        </Hidden>
      ) : null}

      {actions ? <>{actions}</> : null}
    </Stack>
  );
};
